// AIDEV-NOTE: the live poll's guest token (TECH-867 § Answering "Guest token"; CONTRACT § 4.10): minted by
// `POST /live/{code}/open` for a signed-out viewer, kept in localStorage per live code, and sent as
// `X-Live-Guest-Token` on `/me`, `/open` and `/answer` only (never `/state`: the edge cache keys on its URL).
// Not a cookie: the API is Bearer-only. A cleared or private browser gets a new one (accepted risk).

/** The header the backend reads the guest token from. */
export const GUEST_TOKEN_HEADER = 'X-Live-Guest-Token';

/** Every live poll's guest token is stored under this prefix plus its code. */
export const GUEST_TOKEN_STORAGE_PREFIX = 'meetball.live.guest.';

const storageKey = (code: string) => `${GUEST_TOKEN_STORAGE_PREFIX}${code}`;

/** Storage blocked (private mode, blocked site data): the token still answers for this page's life. */
const inMemory = new Map<string, string>();

/**
 * The token this browser holds for one live poll, or null. Memory first: a storage whose `setItem` threw (quota
 * full) may still answer `getItem` with nothing, and the token kept for this page must still be sent.
 */
export const readGuestToken = (code: string): string | null => {
  const kept = inMemory.get(code);
  if (kept) return kept;
  try {
    const value = localStorage.getItem(storageKey(code));
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

/** Keeps a token `/open` minted; when storage refuses it, for this page only (the next visit mints again). */
export const storeGuestToken = (code: string, token: string): void => {
  try {
    localStorage.setItem(storageKey(code), token);
    inMemory.delete(code);
  } catch {
    // Private mode, blocked site data or a full quota: answers still count for this page's life.
    inMemory.set(code, token);
  }
};

/** Drops a token the server no longer knows (an answer refused 401), so the next `/open` mints a new one. */
export const forgetGuestToken = (code: string): void => {
  inMemory.delete(code);
  try {
    localStorage.removeItem(storageKey(code));
  } catch {
    // Storage blocked: nothing was kept there.
  }
};

/**
 * Forgets every live poll's guest token, stored and in memory: sign-out on a shared device must not leave the next
 * person answering, or attaching answers, as this guest (FE-L3b review). Called by the sign-out storage sweeps.
 */
export const forgetAllGuestTokens = (): void => {
  inMemory.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(GUEST_TOKEN_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch {
    // Storage blocked: nothing was kept there.
  }
};

/** The header object for one call: empty without a token. */
export const guestTokenHeaders = (token: string | null | undefined): Record<string, string> =>
  token ? { [GUEST_TOKEN_HEADER]: token } : {};
